import React from 'react';
import useMediaQuery from '@mui/material/useMediaQuery';
import {Theme} from '@mui/material/styles';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import Link from '@mui/material/Link';
import Hidden from '@mui/material/Hidden';
import {
  PromotionListItem,
  getPromotionFormattedDateRange,
} from '@practicegenius/domain/src/data/promotions/promotion';
import {SxImg} from '@practicegenius/pg-ui/src/base/Material/components/StyleableBaseComponents';
import {makeSxStyles} from '@practicegenius/pg-ui/src/base/Material/utils/sx-helpers';
import lang from '../lang';
import {ActionButton} from './sharedUI';

const useStyles = makeSxStyles(theme => ({
  image: {
    width: '100%',
  },
  date: {
    color: theme.palette.text.primary,
    textTransform: 'none',
  },
  // This works in all webkit browsers and even Firefox!
  clampToTwoLines: {
    display: '-webkit-box',
    WebkitLineClamp: 2,
    '-webkit-box-orient': 'vertical',
    overflow: 'hidden',
  },
  title: {
    color: 'var(--template-color-1)',
    textDecoration: 'none',
  },
}));

type Props = {
  promotion: PromotionListItem;
};

export const HighlightedPromotion: React.FC<Props> = ({promotion}) => {
  const [styles] = useStyles();
  const isMediumDown = useMediaQuery<Theme>(theme =>
    theme.breakpoints.down('lg'),
  );

  const img = (
    <SxImg sx={styles.image} src={promotion.image} alt={promotion.title} />
  );

  return (
    <Grid container spacing={4}>
      <Grid item xs={12} md={6}>
        {promotion.url ? <a href={promotion.url || undefined}>{img}</a> : img}
      </Grid>
      <Grid item xs={12} md={6}>
        <Box mb={isMediumDown ? 1.5 : 2}>
          <Typography
            sx={styles.clampToTwoLines}
            variant={isMediumDown ? 'h5' : 'h3'}
          >
            {promotion.url ? (
              <Link href={promotion.url || undefined} sx={styles.title}>
                {promotion.title}
              </Link>
            ) : (
              promotion.title
            )}
          </Typography>
        </Box>
        <Box mb={isMediumDown ? 1 : 1.5}>
          <Typography
            sx={styles.date}
            variant={isMediumDown ? 'subtitle1' : 'h4'}
          >
            {getPromotionFormattedDateRange(promotion)}
          </Typography>
        </Box>
        <Hidden mdDown>
          <Box mb={isMediumDown ? 1 : 1.5}>
            <Typography
              sx={styles.clampToTwoLines}
              variant={isMediumDown ? 'body2' : 'h6'}
              color="textSecondary"
            >
              {promotion.shortDescription}
            </Typography>
          </Box>
        </Hidden>
        <Box>
          <ActionButton
            href={promotion.url ?? undefined}
            data-no-ajax-load-page
            sx={{
              color: 'var(--template-color-1)',
              '&:hover': {color: 'var(--template-color-1)'},
            }}
          >
            {lang.main.highlighted.actionButton}
          </ActionButton>
        </Box>
      </Grid>
    </Grid>
  );
};
